import type { PolicySection } from '../../types/policy';
import { company as c } from '../company';
import { changesSection } from './changes';

/** Sections that follow the coverage table on the Warranty page. */
export const warrantySections: PolicySection[] = [
  {
    id: 'products',
    heading: 'Covered products',
    blocks: [
      `This Limited Warranty is provided by ${c.legalName} to the original retail purchaser of M1 PRO Wireless RGB Lights and Magnetic Mount System bases bought new directly from ${c.website}. Products purchased from unauthorized resellers, marketplaces, auctions or second-hand are not covered.`,
    ],
  },
  {
    id: 'period',
    heading: 'Warranty period',
    blocks: [
      'M1 PRO lights and Magnetic Mount System bases purchased as a single light or a 2-light Duo System carry a 1-Year Limited Warranty from the date of delivery. Lights and bases purchased as part of a 4, 6, 8, 12 or 24-light system carry a Limited Lifetime Warranty, meaning coverage for as long as the original purchaser owns the product. Battery coverage is limited as described below. Coverage is limited to the original purchaser and is not transferable.',
    ],
  },
  {
    id: 'covered',
    heading: 'What is covered',
    blocks: [
      'Defects in materials and workmanship under normal residential use, installed and operated according to the included instructions, including lights that arrive defective or fail to power on, charge or illuminate.',
    ],
  },
  {
    id: 'battery',
    heading: 'Rechargeable battery',
    blocks: [
      `The built-in rechargeable battery is covered against defects for ${c.batteryCoverageYears} year from delivery. Like all lithium batteries, capacity and run time naturally decrease with age, charge cycles and temperature. Gradual reduction in battery capacity or run time is normal wear and is not a defect.`,
    ],
  },
  {
    id: 'exclusions',
    heading: 'What is not covered',
    blocks: [
      {
        list: [
          'Accidental damage, drops, impacts, crushing, or damage from pool cleaners, equipment or pets.',
          'Misuse, abuse, neglect, or use contrary to the instructions or our Product Safety & Use page.',
          'Charging while wet, with a damaged or open port cover, or with non-standard chargers.',
          'Water intrusion caused by an open, damaged or improperly seated USB-C cover, or use beyond the advertised protection rating.',
          'Damage from improper water chemistry, shock treatments, salt systems, chemical exposure, freezing, or extreme heat.',
          'Improper installation, unsupported surfaces, mounting methods other than those described in the instructions, or failure to use the included waterproof sealant and allow the full 12-hour cure before submerging.',
          'Detachment, loss or damage of a base or light caused by an incomplete sealant cure, unclean or unsuitable surfaces, pool cleaners, swimmers, or force applied to the light.',
          'Damage to pool surfaces, tiles, liners or finishes from installing or removing the base or sealant.',
          'Modification, disassembly, or repair by anyone other than us.',
          'Cosmetic wear, discoloration, fading, scratches or calcium/mineral deposits that do not affect function.',
          'Normal battery capacity reduction after the first year.',
          'Commercial, rental or public-facility use.',
          'Lost or stolen products, and products without valid proof of purchase.',
          'Labor, installation, removal, pool draining or diving costs.',
        ],
      },
    ],
  },
  {
    id: 'procedure',
    heading: 'How to make a claim',
    blocks: [
      'Submit a warranty claim through our Warranty Claim page with your order number, a description of the issue, and a clear photo and short video showing the problem. We may ask you to perform basic troubleshooting (full charge with the included USB-C cable, reset, port-cover check) before a claim is approved. If the claim appears eligible, we will issue a Return Authorization (RA) number and return instructions.',
      'Products sent to us without an RA number, without proof of purchase, or to any address other than the one we provide may be refused, returned at your expense, or disposed of after 30 days, and will not be eligible for warranty service.',
    ],
  },
  {
    id: 'proof',
    heading: 'Proof of purchase',
    blocks: [
      `Your ${c.brand} order number is required for every claim. The serial number, packaging or order record must match the product submitted. Claims may be made only by the original purchaser named on the order.`,
    ],
  },
  {
    id: 'shipping',
    heading: 'Return of the defective product',
    blocks: [
      'To receive warranty service you must first return the defective product to us. You are responsible for packaging the product securely and for all costs of shipping it to us, including postage, insurance and any carrier fees. We strongly recommend a trackable, insured shipping method; we are not responsible for products lost or damaged on their way to us.',
      'Include the complete defective light and/or Magnetic Mount System base. Claims for a light must include the light itself — photos alone, or a returned base or packaging without the light, do not qualify for a replacement.',
      'Once we receive and approve your return, we ship the replacement to you at our expense via standard USPS shipping within the United States.',
    ],
  },
  {
    id: 'inspection',
    heading: 'Inspection before replacement',
    blocks: [
      'Replacements are sent only after we have received and inspected the returned product. We do not ship advance replacements. Our inspection determines whether the failure was caused by a defect in materials or workmanship, or by an excluded cause.',
      'If inspection shows the product is not defective, or that the damage was caused by impact, crushing, cracks, punctures, tool marks, burns, chemical exposure, charging while wet, an open or damaged port cover, disassembly, modification or any other exclusion listed above, the claim will be denied. In that case you may (a) have the product returned to you at your expense, or (b) authorize us to recycle it. Products not claimed within 30 days of a denial notice will be recycled.',
      'Our inspection findings are final for purposes of this warranty, subject to your rights under the dispute provisions of our Terms of Service.',
    ],
  },
  {
    id: 'abuse',
    heading: 'Warranty fraud and abuse',
    blocks: [
      'We reserve the right to deny any claim, and to void warranty coverage for the entire order, if we reasonably determine that:',
      {
        list: [
          'the damage was intentional or the claim misrepresents how the damage occurred;',
          'the product returned is not the product originally sold to you, or parts have been swapped;',
          'the claim is made for a product purchased by someone else, or resold or transferred;',
          'photos, videos, order details or other evidence were altered or misrepresented;',
          'a chargeback or payment dispute has been filed for the same order;',
          'an unusual number or pattern of claims indicates abuse of this warranty.',
        ],
      },
      'Each defective unit may be replaced under warranty once in any 12-month period unless we agree otherwise in writing. We may refuse future sales to customers who abuse the warranty or return process.',
    ],
  },
  {
    id: 'remedies',
    heading: 'Remedies',
    blocks: [
      'For a covered defect, we will, at our option, replace the product with a new or refurbished unit of the same model, or with a comparable model of equal or better function if the original is unavailable. If replacement is not commercially practicable, we may instead refund the price you paid for the defective unit. Replacement products are covered for the remainder of the original warranty period. THIS IS YOUR SOLE AND EXCLUSIVE REMEDY UNDER THIS WARRANTY.',
    ],
  },
  {
    id: 'limits',
    heading: 'Limitations',
    blocks: [
      'ALL IMPLIED WARRANTIES, INCLUDING MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE, ARE LIMITED IN DURATION TO THE APPLICABLE WARRANTY PERIOD ABOVE. WE ARE NOT LIABLE FOR INCIDENTAL OR CONSEQUENTIAL DAMAGES, INCLUDING DAMAGE TO POOLS, LINERS, FINISHES, BOATS OR OTHER PROPERTY. Some states do not allow limitations on how long an implied warranty lasts or the exclusion of incidental or consequential damages, so these limitations may not apply to you.',
    ],
  },
  {
    id: 'territory',
    heading: 'Where coverage applies',
    blocks: ['This warranty applies to products purchased and used in the United States.'],
  },
  {
    id: 'rights',
    heading: 'Your legal rights',
    blocks: [
      'This warranty gives you specific legal rights, and you may also have other rights which vary from state to state. Disputes under this warranty are subject to the informal-resolution and arbitration provisions of our Terms of Service.',
    ],
  },
  {
    ...changesSection('Limited Warranty'),
    blocks: [
      ...changesSection('Limited Warranty').blocks,
      'Any change applies to products purchased after the change is posted; the warranty terms in effect on the date of your purchase govern the products in that order.',
    ],
  },
  {
    id: 'contact',
    heading: 'Warrantor',
    blocks: [`${c.legalName}${c.mailingAddress ? ` · ${c.mailingAddress}` : ''} · ${c.supportEmail}`],
  },
];
